import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useState } from "react";
import { useChildEvents } from "../../hooks/events/useChildEvents";
import { useUnsubscribeEvent } from "../../hooks/events/useUnsubscribeEvent";
import { useSubscribeToEvent } from "../../hooks/events/useSubscribeToEvent";
import { useUserAuth } from "../../context/UserAuthContext";
import theme from "../../styles/styleTheme";
import { useTranslation } from "react-i18next";

type TimeslotsProps = {
  event: any;
  openDialog: any;
  isLoadingSubscribe: boolean;
  setIsLoadingSubscribe: any;
};

const Timeslots = ({
  event,
  openDialog,
  isLoadingSubscribe,
  setIsLoadingSubscribe,
}: TimeslotsProps) => {
  moment.locale("nl");

  const { user, reloadUserData } = useUserAuth();
  const { subscribeToEvent } = useSubscribeToEvent();
  const { unsubscribeEvent } = useUnsubscribeEvent();
  const {
    getDataById: getTimeslots,
    data: timeslots,
    loading: loadingTimeslots,
  } = useChildEvents();
  const { t } = useTranslation();
  const [isSubscribedToAnyTimeslot, setIsSubscribedToAnyTimeslot] =
    useState<boolean>(false);

  const handleBookTimeslot = (
    timeslotId: number,
    title: string,
    text: string,
  ) => {
    const subscribe = async () => {
      setIsLoadingSubscribe(true);
      await subscribeToEvent(user?.id, Number(timeslotId)).then(async () => {
        await reloadUserData();
      });
      setIsLoadingSubscribe(false);
    };
    openDialog(title, text, subscribe);
  };

  const handleUnsubscribeTimeslot = (timeslotId: number) => {
    const unsubscribe = async () => {
      setIsLoadingSubscribe(true);
      await unsubscribeEvent(user?.id, Number(timeslotId)).then(async () => {
        await reloadUserData();
        setIsSubscribedToAnyTimeslot(false);
        setIsLoadingSubscribe(false);
      });
    };
    openDialog(
      t("timeslots.unsubscribe"),
      t("timeslots.openDialog.text"),
      unsubscribe,
    );
  };

  useEffect(() => {
    if (!loadingTimeslots && user) {
      const isSubscribed = timeslots?.some((timeslot: any) =>
        user.tenant.attendingEvents.includes(timeslot.id),
      );
      if (!isSubscribedToAnyTimeslot) {
        setIsSubscribedToAnyTimeslot(isSubscribed);
      }
    }
  }, [loadingTimeslots, user]);

  useEffect(() => {
    if (event != null) {
      getTimeslots(event.id);

      const intervalId = setInterval(() => {
        getTimeslots(event.id);
      }, 60000);

      // Cleanup function to clear the interval
      return () => clearInterval(intervalId);
    }
  }, [event, user]);

  return (
    <>
      {!isLoadingSubscribe && (
        <>
          {timeslots.map((timeslot: any, index: number) => (
            <Box key={index}>
              {user.tenant.attendingEvents.includes(timeslot.id) ? (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    sx={{
                      borderRadius: ".5rem",
                      "&:hover": {
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.light,
                      },
                    }}
                    onClick={() => {
                      handleUnsubscribeTimeslot(timeslot.id);
                    }}
                  >
                    <Box>
                      <Typography variant="body2">
                        {moment.utc(timeslot.startDateTime).format("HH:mm")} tot{" "}
                        {moment.utc(timeslot.endDateTime).format("HH:mm")}
                      </Typography>
                      <Typography variant="body2">
                        {t("timeslots.unsubscribe")}
                      </Typography>
                    </Box>
                  </Button>
                </>
              ) : (
                <Button
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.main,
                    "&:hover": {
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.light,
                    },
                  }}
                  style={{
                    borderRadius: ".5rem",
                    display:
                      user.tenant.attendingEvents.includes(timeslot.id) ||
                      timeslot.maxCandidates - timeslot.currentCandidates > 0
                        ? ""
                        : "none",
                  }}
                  fullWidth
                  disabled={
                    (isSubscribedToAnyTimeslot &&
                      !user.tenant.attendingEvents.includes(timeslot.id)) ||
                    timeslot.maxCandidates - timeslot.currentCandidates <= 0
                  }
                  onClick={() =>
                    handleBookTimeslot(
                      timeslot.id,
                      t("timeslots.handleBookTimeslot.title"),
                      `${t("timeslots.handleBookTimeslot.text")} ${moment
                        .utc(timeslot.startDateTime)
                        .format("HH:mm")} ${t(
                        "timeslots.handleBookTimeslot.to",
                      )} ${moment.utc(timeslot.endDateTime).format("HH:mm")}?`,
                    )
                  }
                >
                  <Box>
                    <Typography variant="body2">
                      {moment.utc(timeslot.startDateTime).format("HH:mm")} -{" "}
                      {moment.utc(timeslot.endDateTime).format("HH:mm")}
                    </Typography>
                    <Typography variant="body2">
                      {timeslot.maxCandidates - timeslot.currentCandidates}{" "}
                      {t("timeslots.available-timeslots")}
                    </Typography>
                  </Box>
                </Button>
              )}
            </Box>
          ))}
        </>
      )}
    </>
  );
};

export default Timeslots;
